import { atom, useRecoilState, useRecoilValue } from 'recoil';
import {
  dimensionsAtom,
  TComplimentaryGroup,
  TComplimentaryProductInfo,
} from 'pages/dimensions/store/dimensions';
import { useContext } from 'react';
import { getTotalCostComplimentaryProducts } from 'common/helpers/get-total-cost-complimentary-products.helper';
import { DataContext } from '../../../entries/common/widgets/context';
import { getPrice } from '../../../common/helpers/get-price.helper';
import { TComplimentaryProductsState } from '../types/complimentary-products-state.type';
import { getCheapestProduct } from '../helpers/get-cheapest-product';
import { getTintPrice } from '../helpers/get-tint-price.helper';
import { tintServiceIsAnyColorsSelected } from './tint-service';

const defaultValues = {
  count: '',
  addToCartIsSuccessful: false,
};

export const calculationState = atom({
  key: 'calculation',
  default: defaultValues,
});

export const complimentaryProductsState = atom({
  key: 'complimentaryProducts',
  default: {} as TComplimentaryProductsState,
});

export const ZERO_PRICE = 0;

export const useCalculationState = () => {
  const remoteData = useContext(DataContext);

  const dimensions = useRecoilValue(dimensionsAtom);
  const [calculationFormState, setCalculationState] = useRecoilState(calculationState);
  const [complimentaryProducts, setComplimentaryProducts] = useRecoilState(complimentaryProductsState);
  const { items, complimentaryProductInfo, checkedProducts, complimentaryGroups } = dimensions;
  const isAnyColorsSelected = useRecoilValue(tintServiceIsAnyColorsSelected);
  const tintPrice =
    getTintPrice({ volume: remoteData?.weight, count: calculationFormState.count }) ?? ZERO_PRICE;
  const totalTintPrice = isAnyColorsSelected ? tintPrice : ZERO_PRICE;
  const totalComplimentaryProductCount = complimentaryProductInfo.length + complimentaryGroups.length;

  if (Object.keys(complimentaryProducts).length === 0 && totalComplimentaryProductCount > 0) {
    const complimentaryProductInfoMap = (complimentaryProductInfo as TComplimentaryProductInfo[]).reduce(
      (acc: TComplimentaryProductsState, currentValue: TComplimentaryProductInfo) => {
        acc[currentValue.productId] = { ...currentValue, isSelectedAsVariant: false };
        return acc;
      },
      {}
    );
    const complimentaryGroupsMap = complimentaryGroups.reduce(
      (acc: TComplimentaryProductsState, currentValue: TComplimentaryGroup) => {
        const cheapestProduct = getCheapestProduct(currentValue.products);
        acc[cheapestProduct.productId] = {
          ...cheapestProduct,
          isSelectedAsVariant: currentValue.products.length > 1,
        };
        return acc;
      },
      {}
    );
    const complimentaryProductsMap = Object.assign(complimentaryProductInfoMap, complimentaryGroupsMap);
    setComplimentaryProducts(complimentaryProductsMap);
  }

  const totalPriceOfComplimentaryProducts = totalComplimentaryProductCount
    ? getTotalCostComplimentaryProducts({ complimentaryProducts, checkedProducts })
    : 0;

  const calculatedPrice = remoteData
    ? getPrice({
        mainPrice: remoteData.mainPrice,
        numberOfItemsInStringFormat: calculationFormState.count,
        additionalAsMain: remoteData?.additionalAsMain,
        additionalPrice: remoteData.additionalPrice,
      })
    : ZERO_PRICE;

  const totalAmount = calculatedPrice + totalPriceOfComplimentaryProducts + totalTintPrice;

  const {
    total: { requiredQuantity },
  } = items[0];
  if (!calculationFormState.count && requiredQuantity) {
    setCalculationState(prevState => ({
      ...prevState,
      count: requiredQuantity.toString(),
    }));
  }

  return {
    calculationFormState,
    setCalculationState,
    calculatedPrice,
    totalAmount,
    complimentaryProducts,
    setComplimentaryProducts,
    totalComplimentaryProductCount,
  };
};
